/**
 * MD3 Color Tokens — Flat Rock Global brand palette.
 *
 * Source colors:
 *   Dark Blue:   #001132
 *   Medium Blue: #21376C
 *   Light Blue:  #3B5398
 *   Medium Teal: #3AAD92
 *   Dark Teal:   #236A59
 *   Light Teal:  #45C1A4
 *
 * See docs/architecture/theme-token-roles.md for what each token controls.
 */

export interface ColorTokens {
  // Primary
  primary: string;
  onPrimary: string;
  primaryContainer: string;
  onPrimaryContainer: string;

  // Secondary
  secondary: string;
  onSecondary: string;
  secondaryContainer: string;
  onSecondaryContainer: string;

  // Tertiary
  tertiary: string;
  onTertiary: string;
  tertiaryContainer: string;
  onTertiaryContainer: string;

  // Error
  error: string;
  onError: string;
  errorContainer: string;
  onErrorContainer: string;

  // Warning (not standard MD3 — added for MUI compatibility)
  warning: string;
  onWarning: string;

  // Success (not standard MD3 — added for MUI compatibility)
  success: string;
  onSuccess: string;

  // Surface
  surface: string;
  onSurface: string;
  surfaceVariant: string;
  onSurfaceVariant: string;
  surfaceTint: string;
  surfaceDim: string;
  surfaceBright: string;
  surfaceContainerLowest: string;
  surfaceContainerLow: string;
  surfaceContainer: string;
  surfaceContainerHigh: string;
  surfaceContainerHighest: string;

  // Background
  background: string;
  onBackground: string;

  // Outline
  outline: string;
  outlineVariant: string;

  // Inverse
  inverseSurface: string;
  inverseOnSurface: string;
  inversePrimary: string;

  // Utility
  shadow: string;
  scrim: string;

  // Fixed (don't change between light/dark)
  primaryFixed: string;
  primaryFixedDim: string;
  onPrimaryFixed: string;
  onPrimaryFixedVariant: string;
  secondaryFixed: string;
  secondaryFixedDim: string;
  onSecondaryFixed: string;
  onSecondaryFixedVariant: string;
  tertiaryFixed: string;
  tertiaryFixedDim: string;
  onTertiaryFixed: string;
  onTertiaryFixedVariant: string;
}

/**
 * Light theme colors.
 *
 * Primary = Medium Blue (#21376C) — dark enough for buttons/actions on white
 * Secondary = Light Blue (#3B5398) — supporting actions, filter chips
 * Tertiary = Dark Teal (#236A59) — contrasting accent for focus/badges
 * Surfaces = cool white/gray with subtle blue undertone
 */
export const lightColors: ColorTokens = {
  // Primary — medium blue
  primary: '#21376C',
  onPrimary: '#FFFFFF',
  primaryContainer: '#D4E3FF',
  onPrimaryContainer: '#0A1F4B',

  // Secondary — light blue
  secondary: '#3B5398',
  onSecondary: '#FFFFFF',
  secondaryContainer: '#DBE1FF',
  onSecondaryContainer: '#1B2D62',

  // Tertiary — dark teal
  tertiary: '#236A59',
  onTertiary: '#FFFFFF',
  tertiaryContainer: '#C4F1E4',
  onTertiaryContainer: '#0A3D2F',

  // Error — standard MD3
  error: '#BA1A1A',
  onError: '#FFFFFF',
  errorContainer: '#FFDAD6',
  onErrorContainer: '#410002',

  // Warning — amber tones
  warning: '#7A5900',
  onWarning: '#FFFFFF',

  // Success — green tones
  success: '#146B3A',
  onSuccess: '#FFFFFF',

  // Surface — cool white with blue undertone
  surface: '#F8FAFF',
  onSurface: '#181C24',
  surfaceVariant: '#DFE2EE',
  onSurfaceVariant: '#434750',
  surfaceTint: '#21376C',
  surfaceDim: '#D7DAE4',
  surfaceBright: '#F8FAFF',
  surfaceContainerLowest: '#FFFFFF',
  surfaceContainerLow: '#F2F4FC',
  surfaceContainer: '#ECEFF7',
  surfaceContainerHigh: '#E6E9F2',
  surfaceContainerHighest: '#E1E3EC',

  // Background
  background: '#F8FAFF',
  onBackground: '#181C24',

  // Outline
  outline: '#737780',
  outlineVariant: '#C3C6D2',

  // Inverse
  inverseSurface: '#2D3038',
  inverseOnSurface: '#EFF0FA',
  inversePrimary: '#A6C4FF',

  // Utility
  shadow: '#000000',
  scrim: '#000000',

  // Fixed
  primaryFixed: '#D4E3FF',
  primaryFixedDim: '#A6C4FF',
  onPrimaryFixed: '#001132',
  onPrimaryFixedVariant: '#21376C',
  secondaryFixed: '#DBE1FF',
  secondaryFixedDim: '#B3C5FF',
  onSecondaryFixed: '#0D1F4E',
  onSecondaryFixedVariant: '#3B5398',
  tertiaryFixed: '#C4F1E4',
  tertiaryFixedDim: '#7AD5BE',
  onTertiaryFixed: '#002A1F',
  onTertiaryFixedVariant: '#236A59',
};

/**
 * Dark theme colors.
 *
 * Primary becomes lighter (readable on dark backgrounds).
 * Containers become darker. Surfaces are near-black with blue tint.
 * Tertiary uses the bright teal for pop on dark backgrounds.
 */
export const darkColors: ColorTokens = {
  // Primary — lightened blue
  primary: '#A6C4FF',
  onPrimary: '#001132',
  primaryContainer: '#21376C',
  onPrimaryContainer: '#D4E3FF',

  // Secondary — lightened blue
  secondary: '#B3C5FF',
  onSecondary: '#0D1F4E',
  secondaryContainer: '#3B5398',
  onSecondaryContainer: '#DBE1FF',

  // Tertiary — light teal pops on dark
  tertiary: '#45C1A4',
  onTertiary: '#002A1F',
  tertiaryContainer: '#236A59',
  onTertiaryContainer: '#C4F1E4',

  // Error — lighter red for dark bg
  error: '#FFB4AB',
  onError: '#690005',
  errorContainer: '#93000A',
  onErrorContainer: '#FFDAD6',

  // Warning — lightened amber for dark bg
  warning: '#FFBF47',
  onWarning: '#3F2E00',

  // Success — lightened green for dark bg
  success: '#7AD87E',
  onSuccess: '#003910',

  // Surface — very dark with blue undertone
  surface: '#101420',
  onSurface: '#E1E3EC',
  surfaceVariant: '#434750',
  onSurfaceVariant: '#C3C6D2',
  surfaceTint: '#A6C4FF',
  surfaceDim: '#101420',
  surfaceBright: '#363A45',
  surfaceContainerLowest: '#0B0E18',
  surfaceContainerLow: '#181C24',
  surfaceContainer: '#1C2028',
  surfaceContainerHigh: '#262A33',
  surfaceContainerHighest: '#31343E',

  // Background
  background: '#101420',
  onBackground: '#E1E3EC',

  // Outline
  outline: '#8D909A',
  outlineVariant: '#434750',

  // Inverse
  inverseSurface: '#E1E3EC',
  inverseOnSurface: '#2D3038',
  inversePrimary: '#21376C',

  // Utility
  shadow: '#000000',
  scrim: '#000000',

  // Fixed (same as light — they don't change)
  primaryFixed: '#D4E3FF',
  primaryFixedDim: '#A6C4FF',
  onPrimaryFixed: '#001132',
  onPrimaryFixedVariant: '#21376C',
  secondaryFixed: '#DBE1FF',
  secondaryFixedDim: '#B3C5FF',
  onSecondaryFixed: '#0D1F4E',
  onSecondaryFixedVariant: '#3B5398',
  tertiaryFixed: '#C4F1E4',
  tertiaryFixedDim: '#7AD5BE',
  onTertiaryFixed: '#002A1F',
  onTertiaryFixedVariant: '#236A59',
};
